<template>
  <div
    v-if="visible"
    class="payment-issue-banner m-4"
    role="alert"
    data-testid="payment-issue-banner"
  >
    <div class="payment-issue-banner__header">
      <AlertCircle :size="20" :stroke-width="2" class="payment-issue-banner__icon" />
      <h3 class="payment-issue-banner__title">Your subscription payment failed</h3>
      <button
        type="button"
        class="payment-issue-banner__dismiss"
        aria-label="Dismiss payment notice"
        data-testid="payment-issue-banner-dismiss"
        @click="onDismiss"
      >
        <X :size="18" :stroke-width="2" />
      </button>
    </div>
    <p class="payment-issue-banner__body" data-testid="payment-issue-banner-body">{{ body }}</p>
    <router-link
      to="/billing/subscription"
      class="btn btn-primary payment-issue-banner__action"
      data-testid="payment-issue-banner-link"
    >
      View your subscription
    </router-link>
  </div>
</template>

<script setup lang="ts">
/**
 * The failed-payment notice every organisation admin sees on every signed-in page while their
 * subscription is `past_due` or `payment_failed` (Task #1840, US #1830 R6/R9).
 *
 * Absent entirely, with no request made, while public registration is off (the commercial
 * surfaces ship dark behind that switch) and for anyone who is not an organisation admin. The
 * status comes from the existing `GET /api/billing/subscription`, read again on every route change; a failed read shows nothing
 * (the subscription page itself reports its own errors).
 *
 * The × hides it until the next sign-in (see `payment-issue-banner-dismissal`); the auth store
 * clears that on logout and on a successful sign-in, so it comes back for any new session.
 */
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AlertCircle, X } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth.store'
import { useSeatBilling } from '@/composables/useSeatBilling'
import { isPublicRegistrationEnabled } from '@/utils/public-registration-switch'
import { dismiss, isDismissed } from '@/utils/payment-issue-banner-dismissal'

const authStore = useAuthStore()
const route = useRoute()
const { loadSubscription } = useSeatBilling()

const status = ref<string | null>(null)
// Held in memory as well as in storage, so a blocked localStorage still hides it at once.
const dismissed = ref(isDismissed())

function onDismiss() {
  dismiss()
  dismissed.value = true
}

const eligible = computed(
  () =>
    isPublicRegistrationEnabled() &&
    authStore.isAuthenticated &&
    authStore.hasRole('organisation-admin')
)

// The card is updated in-app: "Manage payment method" on the subscription page (Task #1835).
const body = computed(() =>
  status.value === 'payment_failed'
    ? 'Your purchased seats are paused until a payment succeeds. To update your card, use "Manage payment method" on your subscription page.'
    : 'Stripe will retry it over the next two weeks or so, and your access continues meanwhile. To update your card, use "Manage payment method" on your subscription page.'
)

const visible = computed(
  () =>
    eligible.value &&
    !dismissed.value &&
    (status.value === 'past_due' || status.value === 'payment_failed')
)

// Read again on every route change while eligible, so the notice follows the status within a
// session. Only the latest request may write: an older, slower response is dropped.
let latestRequest = 0
watch(
  [eligible, () => route.fullPath],
  async ([isEligible], [wasEligible]) => {
    const request = ++latestRequest
    if (!isEligible) {
      status.value = null
      return
    }
    if (!wasEligible) status.value = null
    const result = await loadSubscription()
    if (request !== latestRequest) return
    status.value = result.ok ? (result.value.subscription?.status ?? null) : null
  },
  { immediate: true }
)
</script>
